import { Divider } from '@hover/blueprint';
import React, { useContext, useMemo } from 'react';

import { FilteredBySubHeader } from 'src/modules/designStudio/components/DesignMenu/FilteredBySubHeader';
import { MenuHeader } from 'src/modules/designStudio/components/DesignMenu/Menu/MenuHeader';
import {
  EmptyDesignConfig,
  FeatureCategories,
  FrameFilterItems,
  GridFilterItems,
} from 'src/modules/designStudio/constants';
import { useBuildingDesigner } from 'src/modules/designStudio/helpers/machete/useBuildingDesigner';
import { productCatalogContext } from 'src/modules/designStudio/providers';
import type { UpdateDesignFunction } from 'src/modules/designStudio/providers/HistoryProvider';
import type { BuildingAppearances } from 'src/modules/designStudio/providers/MacheteBuildingProvider/types';
import type {
  LegacyDesignConfig,
  WindowStyle,
} from 'src/modules/designStudio/types';
import { WindowParametricProperties } from 'src/modules/designStudio/types';

import { ColorList, WindowFilter } from '../Filters';

const createLegacyWindowDesignConfig = (
  appearances: BuildingAppearances,
): LegacyDesignConfig => {
  const windowAppearance =
    appearances.windowAppearances[appearances.windowAppearances.length - 1];

  const windowStyle: WindowStyle = {
    [WindowParametricProperties.FRAME_TYPE]:
      windowAppearance.basicParametricOptions?.frameType ?? null,
    [WindowParametricProperties.GRID_TYPE]:
      windowAppearance.basicParametricOptions?.grilleType ?? null,
    color: windowAppearance.color ?? null,
  };

  return {
    ...EmptyDesignConfig,
    WINDOWS: windowStyle,
  };
};

export const WindowMenu = ({
  requestingOrgName,
  headerText,
  closeMenu,
}: {
  requestingOrgName?: string;
  headerText: string | null;
  closeMenu: () => void;
}) => {
  const { buildingAppearances, mutateBuilding } = useBuildingDesigner();

  const designConfig = useMemo(
    () => createLegacyWindowDesignConfig(buildingAppearances),
    [buildingAppearances],
  );

  const updateDesign: UpdateDesignFunction = ({ update }) => {
    mutateBuilding(async mutations => {
      const windowStyle = update as {
        frameType: string;
        gridType: string;
        color: string;
      };
      const frameType = windowStyle.frameType ?? 'casement';
      const grilleType = windowStyle.gridType ?? 'none';
      const newAppearance = {
        basicParametricOptions: {
          frameType,
          grilleType,
        },
        color: windowStyle.color ?? null,
      };
      await mutations.changeWindowAppearances([{}], newAppearance);
    });
  };

  const { color } = designConfig[FeatureCategories.WINDOW] as {
    color: string | null;
  };

  const { filteredColors, filterTracker } = useContext(productCatalogContext);

  const isWindowConfigEmpty =
    !!designConfig.WINDOWS[WindowParametricProperties.GRID_TYPE];

  const subHeader =
    requestingOrgName && filterTracker[FeatureCategories.TRIM] ? (
      <FilteredBySubHeader companyName={requestingOrgName} />
    ) : undefined;

  return (
    <>
      <MenuHeader closeMenu={closeMenu} headerText={headerText ?? ''} />
      <WindowFilter
        category="Window Types"
        designConfig={designConfig}
        items={FrameFilterItems}
        subHeader={subHeader}
        updateDesign={updateDesign}
        windowDesignConfigProperty={WindowParametricProperties.FRAME_TYPE}
      />
      <Divider orientation="horizontal" />
      {!isWindowConfigEmpty ? null : (
        <>
          <WindowFilter
            category="Grid Style"
            designConfig={designConfig}
            items={GridFilterItems}
            updateDesign={updateDesign}
            windowDesignConfigProperty={WindowParametricProperties.GRID_TYPE}
          />
          <Divider orientation="horizontal" />
          <ColorList
            color={color}
            colorData={filteredColors[FeatureCategories.WINDOW]}
            designConfig={designConfig}
            designConfigCategory={FeatureCategories.WINDOW}
            updateDesign={updateDesign}
          />
          <Divider orientation="horizontal" />
        </>
      )}
    </>
  );
};
